import { Splide } from "@splidejs/splide";
import { servicesSlider } from "../sliders/sliders";
import { createPopper } from "@popperjs/core";
import { gsap } from "gsap";

export default class SwitchServices {
    private section: HTMLElement;
    private sliderWrapper: HTMLElement | null;
    private mainIconsWrapper: HTMLElement[] | null;
    private serviceElements: HTMLElement[] | null;
    private sliderElements: HTMLElement[] | null;
    private breakpoint: string;
    private slider: Splide | null = null;
    private activeSlide: HTMLElement | null = null;

    constructor(section: HTMLElement) {
        this.section = section;
        this.sliderWrapper = this.section.querySelector("#services-slider");
        this.sliderElements = [...this.section?.querySelectorAll<HTMLElement>(".content-slider-item")];
        this.mainIconsWrapper = [...this.section.querySelectorAll<HTMLElement>(".service-icon")];
        this.serviceElements = [...this.section.querySelectorAll<HTMLElement>(".services-item")];
        this.breakpoint = "(max-width: 1480px)";

        this.init();
    }

    public init = async (): Promise<void> => {
        if (!this.sliderWrapper) return;

        this.slider = servicesSlider(this.sliderWrapper);

        this.slider.on("active", () => {
            this.findActiveSlide();

            if (!this.activeSlide) return;

            this.changeMainIcon(this.activeSlide?.dataset.slide as string);
            this.setActiveService(this.activeSlide?.dataset.slide as string);
        });

        this.serviceElements?.forEach((service) => {
            service.addEventListener("click", (e) => {
                const currentService = e?.currentTarget as HTMLElement;
                this.setActiveService(currentService.dataset.service as string);
                this.changeMainIcon(currentService.dataset.service as string);
                this.setActiveSlider(currentService.dataset.service as string);
            });
        });

        window.matchMedia(this.breakpoint).addEventListener("change", this.createToolTips);

        this.createToolTips();
    };

    private findActiveSlide = () => {
        const activeSliders = this.sliderElements?.filter((slide) => slide.classList.contains("is-active")) as HTMLElement[];

        this.activeSlide = activeSliders[0];
    };

    private setActiveService = (dataAtr: string) => {
        if (!this.serviceElements) return;

        this.serviceElements?.forEach((service) => {
            service.classList.remove("services-item--active");

            if (dataAtr === service.dataset.service) service.classList.add("services-item--active");
        });
    };

    private changeMainIcon = (dataAtr: string) => {
        if (!this.mainIconsWrapper) return;

        this.mainIconsWrapper?.forEach((icon) => {
            icon.classList.remove("service-icon--active");

            if (dataAtr === icon.dataset.icon) icon.classList.add("service-icon--active");
        });
    };

    private setActiveSlider = (dataAtr: string) => {
        if (!this.sliderElements) return;

        this.sliderElements.forEach((slide, index) => {
            if (dataAtr === slide.dataset.slide) this.slider?.go(index);
        });
    };

    private createToolTips = (event?: MediaQueryListEvent) => {
        this.serviceElements?.forEach((service, index) => {
            const tooltip = service.querySelector(".services-item--content") as HTMLElement;

            const mediaQuery = event ? event.matches : window.matchMedia(this.breakpoint).matches;

            if (index === 0) {
                createPopper(service, tooltip, {
                    placement: "right-end",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [mediaQuery ? -110 : -148, mediaQuery ? -25 : -67],
                            },
                        },
                        {
                            name: "flip",
                            enabled: false,
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                mainAxis: false,
                            },
                        },
                    ],
                });
            } else if (index === 1) {
                createPopper(service, tooltip, {
                    placement: "top",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, mediaQuery ? 10 : 35],
                            },
                        },
                        {
                            name: "flip",
                            enabled: false,
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                mainAxis: false,
                            },
                        },
                    ],
                });
            } else if (index === 2) {
                createPopper(service, tooltip, {
                    placement: mediaQuery ? "top-start" : "left-end",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [mediaQuery ? -30 : -144, mediaQuery ? 5 : -2],
                            },
                        },
                        {
                            name: "flip",
                            enabled: false,
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                mainAxis: false,
                            },
                        },
                    ],
                });
            } else if (index === 3) {
                createPopper(service, tooltip, {
                    placement: mediaQuery ? "top-start" : "left-start",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [mediaQuery ? -40 : -13, mediaQuery ? 5 : 45],
                            },
                        },
                        {
                            name: "flip",
                            enabled: false,
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                mainAxis: false,
                            },
                        },
                    ],
                });
            } else if (index === 4) {
                createPopper(service, tooltip, {
                    placement: mediaQuery ? "top-start" : "left",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [mediaQuery ? -30 : 0, mediaQuery ? 5 : 43],
                            },
                        },
                        {
                            name: "flip",
                            enabled: false,
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                mainAxis: false,
                            },
                        },
                    ],
                });
            } else if (index === 5) {
                createPopper(service, tooltip, {
                    placement: mediaQuery ? "top-start" : "left-start",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [mediaQuery ? -50 : 94, mediaQuery ? 5 : 35],
                            },
                        },
                        {
                            name: "flip",
                            enabled: false,
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                mainAxis: false,
                            },
                        },
                    ],
                });
            } else if (index === 6) {
                createPopper(service, tooltip, {
                    placement: mediaQuery ? "top-start" : "bottom-start",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [mediaQuery ? -50 : -134, mediaQuery ? 5 : 17],
                            },
                        },
                        {
                            name: "flip",
                            enabled: false,
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                mainAxis: false,
                            },
                        },
                    ],
                });
            } else if (index === 7) {
                createPopper(service, tooltip, {
                    placement: mediaQuery ? "top" : "bottom",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [mediaQuery ? 0 : -1, mediaQuery ? 5 : 35],
                            },
                        },
                        {
                            name: "flip",
                            enabled: false,
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                mainAxis: false,
                            },
                        },
                    ],
                });
            }
        });
    };
}
